// Pure derivation of the competition layer: (estimates + members + module
// config + user) → live module states + resolved historical wins. No side
// effects, no UI — sibling of advisor-stats.ts, which folds the wins into
// the wallet event stream.
//
// "Scan" = estimate created, attributed via `e.createdBy?.id`. Advisors are
// members with the Service Advisor role; estimates from anyone else are
// ignored so managers testing the tool don't pollute the race.
//
// Wins resolve over the trailing 8 weeks of CLOSED periods only — the
// still-open day/week never pays (except streakBonus, which fires the day
// the streak length is reached, once per achieved run).

import type {
  ApiEstimate,
  CompetitionModuleConfig,
  CompetitionModuleKey,
  TeamMemberSummary,
} from "@/lib/api/types"
import { COMPETITION_DEFAULTS } from "@/lib/dashboard/defaults"
import type { CompetitionCadence } from "@/lib/dashboard/defaults"
import {
  WORKDAY_INDICES,
  dateKey,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "@/lib/dashboard/advisor-stats"
import { Roles } from "@/lib/team/roles"

export interface CompetitionLiveState {
  key: CompetitionModuleKey
  title: string
  description: string
  prize: number
  cadence: CompetitionCadence
  // 0–100 towards the module's finish line; null when the module has no
  // meaningful bar for the current user (e.g. first-scan already claimed).
  progressPct: number | null
  progressLabel: string
  // Where the current user stands right now ("You're #2", "Maria K. got
  // it"); null when there's nothing to stand on yet.
  standingLabel: string | null
  endsLabel: string
  // One actionable line ("2 more scans to pass Maria K."); null when
  // there's no useful next action.
  nudge: string | null
}

export interface CompetitionWin {
  key: CompetitionModuleKey
  userId: number
  amount: number
  periodKey: string
  wonAt: Date
  label: string
}

interface CompetitionStateInput {
  estimates: ApiEstimate[]
  members: TeamMemberSummary[] | undefined
  competitions: Partial<Record<CompetitionModuleKey, CompetitionModuleConfig>> | undefined
  currentUserId: number | null | undefined
  // Oldest date whose wins must resolve. The manager /payouts screen can
  // navigate further back than the default trailing-8-week horizon, so it
  // passes the visible period's start here to widen the lookback loops. When
  // omitted (wallet/dashboard callers) the default trailing window is used.
  horizonStart?: Date
  now?: Date
}

const DAY_MS = 24 * 60 * 60 * 1000
const TRAILING_DAYS = 56 // 8 weeks
const TRAILING_WEEKS = 8

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

function shortDate(d: Date): string {
  return `${WEEKDAY_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`
}
function weekOfLabel(weekStart: Date): string {
  return `wk of ${weekStart.getDate()} ${MONTH_SHORT[weekStart.getMonth()]}`
}
function timeLabel(d: Date): string {
  const h24 = d.getHours()
  const h = h24 % 12 === 0 ? 12 : h24 % 12
  const m = String(d.getMinutes()).padStart(2, "0")
  return `${h}:${m}${h24 < 12 ? "am" : "pm"}`
}
function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`
}

interface Scan {
  userId: number
  at: Date
}

interface UserPeriodStats {
  count: number
  first: Date
  last: Date
}

export function computeCompetitionState(input: CompetitionStateInput): {
  live: CompetitionLiveState[]
  wins: CompetitionWin[]
} {
  const now = input.now ?? new Date()
  const today = startOfDay(now)
  const todayKey = dateKey(today)
  const weekStart = startOfWeek(now)
  const me = input.currentUserId ?? null

  // Win-resolution horizon. Defaults to the trailing 8 weeks; widened when a
  // caller (the /payouts screen) needs wins from an older navigable period.
  // Every closed-period loop below sizes itself from these so competitionPay
  // never silently drops to $0 for a period the manager can open.
  const horizon = input.horizonStart ? startOfDay(input.horizonStart) : null
  const dailyLookback = horizon
    ? Math.max(TRAILING_DAYS, Math.ceil((today.getTime() - horizon.getTime()) / DAY_MS) + 1)
    : TRAILING_DAYS
  const weeklyLookback = horizon
    ? Math.max(
        TRAILING_WEEKS,
        Math.ceil((weekStart.getTime() - startOfWeek(horizon).getTime()) / (7 * DAY_MS)) + 1,
      )
    : TRAILING_WEEKS
  const monthlyLookback = horizon
    ? Math.max(
        2,
        (today.getFullYear() - horizon.getFullYear()) * 12 +
          (today.getMonth() - horizon.getMonth()) +
          1,
      )
    : 2

  // Advisor scope. With member data we hard-filter to Service Advisors;
  // without it (loading, mock gaps) any attributed creator counts.
  const advisorIds = input.members
    ? new Set(
        input.members.filter((m) => m.role === Roles.ServiceAdvisor).map((m) => m.id),
      )
    : null

  const nameByUser = new Map<number, { first: string; last: string }>()
  for (const m of input.members ?? []) {
    nameByUser.set(m.id, { first: m.firstName, last: m.lastName })
  }

  const scans: Scan[] = []
  for (const e of input.estimates) {
    const author = e.createdBy
    if (author == null) continue
    if (advisorIds && !advisorIds.has(author.id)) continue
    const at = new Date(e.createdAt)
    if (!Number.isFinite(at.getTime())) continue
    scans.push({ userId: author.id, at })
    if (!nameByUser.has(author.id)) {
      nameByUser.set(author.id, { first: author.firstName, last: author.lastName })
    }
  }
  scans.sort((a, b) => a.at.getTime() - b.at.getTime())

  const shortName = (uid: number): string => {
    const n = nameByUser.get(uid)
    if (!n || !n.first) return "A teammate"
    return n.last ? `${n.first} ${n.last[0]}.` : n.first
  }

  // Per-day and per-week (Mon-start) buckets, with per-user first/last scan
  // times so tie-breaks ("earliest submission in period") and win dates are
  // resolvable without re-walking the estimate list.
  const scansByDay = new Map<string, Scan[]>() // ascending, scans presorted
  const dayStats = new Map<string, Map<number, UserPeriodStats>>()
  const weekStats = new Map<string, Map<number, UserPeriodStats>>()
  const bump = (
    bucket: Map<string, Map<number, UserPeriodStats>>,
    key: string,
    s: Scan,
  ) => {
    let users = bucket.get(key)
    if (!users) {
      users = new Map()
      bucket.set(key, users)
    }
    const cur = users.get(s.userId)
    if (cur) {
      cur.count++
      if (s.at > cur.last) cur.last = s.at
      if (s.at < cur.first) cur.first = s.at
    } else {
      users.set(s.userId, { count: 1, first: s.at, last: s.at })
    }
  }
  for (const s of scans) {
    const dk = dateKey(s.at)
    const list = scansByDay.get(dk)
    if (list) list.push(s)
    else scansByDay.set(dk, [s])
    bump(dayStats, dk, s)
    bump(weekStats, dateKey(startOfWeek(s.at)), s)
  }

  // Per-user week history sorted by week start — beatPersonalBest needs
  // "best week strictly before week W" both live and per closed week.
  const weekHistoryByUser = new Map<number, { weekStartMs: number; count: number }[]>()
  for (const [wk, users] of weekStats) {
    const [y, m, d] = wk.split("-").map(Number)
    const ms = new Date(y, m - 1, d).getTime()
    for (const [uid, st] of users) {
      let hist = weekHistoryByUser.get(uid)
      if (!hist) {
        hist = []
        weekHistoryByUser.set(uid, hist)
      }
      hist.push({ weekStartMs: ms, count: st.count })
    }
  }
  for (const hist of weekHistoryByUser.values()) {
    hist.sort((a, b) => a.weekStartMs - b.weekStartMs)
  }
  const bestWeekBefore = (uid: number, beforeMs: number): number => {
    let best = 0
    for (const w of weekHistoryByUser.get(uid) ?? []) {
      if (w.weekStartMs >= beforeMs) break
      if (w.count > best) best = w.count
    }
    return best
  }

  const countFor = (
    bucket: Map<string, Map<number, UserPeriodStats>>,
    key: string,
    uid: number | null,
  ): number => (uid == null ? 0 : bucket.get(key)?.get(uid)?.count ?? 0)

  const cfgFor = (k: CompetitionModuleKey): CompetitionModuleConfig | undefined =>
    input.competitions?.[k]
  const enabled = (k: CompetitionModuleKey): CompetitionModuleConfig | null => {
    const c = cfgFor(k)
    return c?.enabled ? c : null
  }
  // A cleared threshold field persists as 0 (or a stray negative); treat any
  // non-positive value as "unset" so it falls back to the registry default
  // instead of a met-instantly / divide-by-zero target that would pay every
  // advisor every period and render NaN% progress bars.
  const thresholdFor = (k: CompetitionModuleKey, c: CompetitionModuleConfig): number =>
    (c.threshold != null && c.threshold > 0
      ? c.threshold
      : COMPETITION_DEFAULTS[k].defaultThreshold) ?? 1

  // ── Ends labels ──────────────────────────────────────────────────────
  const endsToday = (): string => {
    const midnight = new Date(today)
    midnight.setDate(midnight.getDate() + 1)
    const hrs = (midnight.getTime() - now.getTime()) / (60 * 60 * 1000)
    if (hrs < 1) return `Ends in ${Math.max(1, Math.round(hrs * 60))}m`
    return `Ends in ${Math.ceil(hrs)}h`
  }
  const endsThisWeek = (): string => {
    const nextMon = new Date(weekStart)
    nextMon.setDate(nextMon.getDate() + 7)
    const daysLeft = Math.round((nextMon.getTime() - today.getTime()) / DAY_MS)
    return daysLeft <= 1 ? "Ends today" : `${daysLeft} days left`
  }
  const endsThisMonth = (): string => {
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)
    const daysLeft = Math.round((nextMonth.getTime() - today.getTime()) / DAY_MS)
    return daysLeft <= 1 ? "Ends today" : `${daysLeft} days left`
  }

  // ── LIVE ─────────────────────────────────────────────────────────────
  const live: CompetitionLiveState[] = []
  const pushLive = (
    key: CompetitionModuleKey,
    c: CompetitionModuleConfig,
    partial: Pick<
      CompetitionLiveState,
      "progressPct" | "progressLabel" | "standingLabel" | "endsLabel" | "nudge"
    > & { description?: string },
  ) => {
    const d = COMPETITION_DEFAULTS[key]
    live.push({
      key,
      title: d.title,
      description: partial.description ?? d.description,
      prize: c.amount,
      cadence: d.cadence,
      progressPct: partial.progressPct,
      progressLabel: partial.progressLabel,
      standingLabel: partial.standingLabel,
      endsLabel: partial.endsLabel,
      nudge: partial.nudge,
    })
  }

  const todayUsers = dayStats.get(todayKey)
  const myToday = countFor(dayStats, todayKey, me)

  {
    const c = enabled("dailyScanChampion")
    if (c) {
      let leader = 0
      let atLeader = 0
      let rank = 1
      for (const [, st] of todayUsers ?? []) {
        if (st.count > leader) leader = st.count
      }
      for (const [, st] of todayUsers ?? []) {
        if (st.count === leader && leader > 0) atLeader++
        if (st.count > myToday) rank++
      }
      const noScans = leader === 0
      pushLive("dailyScanChampion", c, {
        progressPct: leader > 0 ? Math.round((myToday / leader) * 100) : null,
        progressLabel: noScans ? "No scans yet today" : `${plural(myToday, "scan")} today`,
        standingLabel: noScans
          ? null
          : myToday === leader
            ? atLeader > 1
              ? "Tied for #1"
              : "You're #1"
            : `You're #${rank}`,
        endsLabel: endsToday(),
        nudge: noScans
          ? "First scan takes the lead"
          : myToday < leader
            ? `${plural(leader - myToday + 1, "more scan")} to take the lead`
            : null,
      })
    }
  }

  {
    const c = enabled("firstScanOfDay")
    if (c) {
      const first = scansByDay.get(todayKey)?.[0]
      if (!first) {
        pushLive("firstScanOfDay", c, {
          progressPct: null,
          progressLabel: "Still open — no scans yet",
          standingLabel: null,
          endsLabel: endsToday(),
          nudge: `Be first on the board for $${c.amount}`,
        })
      } else if (first.userId === me) {
        pushLive("firstScanOfDay", c, {
          progressPct: 100,
          progressLabel: `Claimed at ${timeLabel(first.at)}`,
          standingLabel: "You got it",
          endsLabel: endsToday(),
          nudge: null,
        })
      } else {
        pushLive("firstScanOfDay", c, {
          progressPct: null,
          progressLabel: `Claimed at ${timeLabel(first.at)}`,
          standingLabel: `${shortName(first.userId)} got it`,
          endsLabel: endsToday(),
          nudge: "Gone for today — be first tomorrow",
        })
      }
    }
  }

  {
    const c = enabled("hotStreak")
    if (c) {
      const th = thresholdFor("hotStreak", c)
      const hit = myToday >= th
      pushLive("hotStreak", c, {
        progressPct: Math.min(100, Math.round((myToday / th) * 100)),
        progressLabel: `${myToday} of ${plural(th, "scan")}`,
        standingLabel: hit ? `Hit — $${c.amount} earned` : null,
        endsLabel: endsToday(),
        nudge: hit ? null : `${plural(th - myToday, "more scan")} today = $${c.amount}`,
      })
    }
  }

  {
    const c = enabled("fastStart")
    if (c) {
      const th = thresholdFor("fastStart", c)
      const winner = firstToReach(scansByDay.get(todayKey) ?? [], th)
      pushLive("fastStart", c, {
        progressPct: winner?.userId === me ? 100 : Math.min(100, Math.round((myToday / th) * 100)),
        progressLabel: `${Math.min(myToday, th)} of ${plural(th, "scan")}`,
        standingLabel: winner
          ? winner.userId === me
            ? "You won it today"
            : `${shortName(winner.userId)} got there first`
          : "Still open",
        endsLabel: endsToday(),
        nudge: winner ? null : `${plural(Math.max(1, th - myToday), "more scan")} wins it`,
      })
    }
  }

  const thisWeekKey = dateKey(weekStart)
  const lastWeekStart = new Date(weekStart)
  lastWeekStart.setDate(lastWeekStart.getDate() - 7)
  const lastWeekKey = dateKey(lastWeekStart)
  const myWeek = countFor(weekStats, thisWeekKey, me)

  {
    const c = enabled("weeklyVolumeLeaderboard")
    if (c) {
      const users = weekStats.get(thisWeekKey) ?? new Map<number, UserPeriodStats>()
      let rank = 1
      let leader = 0
      let above: { uid: number; count: number } | null = null
      for (const [uid, st] of users) {
        if (st.count > leader) leader = st.count
        if (st.count > myWeek) {
          rank++
          if (!above || st.count < above.count) above = { uid, count: st.count }
        }
      }
      const total = advisorIds ? advisorIds.size : Math.max(users.size, rank)
      const qualifier = c.qualifier ?? COMPETITION_DEFAULTS.weeklyVolumeLeaderboard.defaultQualifier
      const description =
        qualifier === "top_3"
          ? `$${c.amount} each to the week's top 3 scanners.`
          : qualifier === "above_target"
            ? `$${c.amount} to every advisor who beats the weekly target.`
            : `$${c.amount} to the week's top scanner.`
      const gap = above ? above.count - myWeek : 0
      pushLive("weeklyVolumeLeaderboard", c, {
        description,
        progressPct: leader > 0 ? Math.round((myWeek / leader) * 100) : null,
        progressLabel: `${plural(myWeek, "scan")} this week`,
        standingLabel: `You're #${rank} of ${Math.max(total, 1)}`,
        endsLabel: endsThisWeek(),
        nudge:
          above && gap <= 5
            ? `${plural(gap + 1, "more scan")} to pass ${shortName(above.uid)}`
            : null,
      })
    }
  }

  {
    const c = enabled("comebackChallenge")
    if (c) {
      const myLast = countFor(weekStats, lastWeekKey, me)
      const myDelta = myWeek - myLast
      // Current best improver this week (positive week-over-week delta).
      let best: { uid: number; delta: number } | null = null
      const uids = new Set<number>([
        ...(weekStats.get(thisWeekKey)?.keys() ?? []),
        ...(weekStats.get(lastWeekKey)?.keys() ?? []),
      ])
      for (const uid of uids) {
        const delta = countFor(weekStats, thisWeekKey, uid) - countFor(weekStats, lastWeekKey, uid)
        if (delta > 0 && (!best || delta > best.delta)) best = { uid, delta }
      }
      pushLive("comebackChallenge", c, {
        progressPct: null,
        progressLabel: `${myWeek} this week · ${myLast} last week`,
        standingLabel: best
          ? best.uid === me
            ? `You lead — up ${best.delta}`
            : `${shortName(best.uid)} leads — up ${best.delta}`
          : "Wide open — anyone can take it",
        endsLabel: endsThisWeek(),
        nudge:
          best && best.uid !== me
            ? `${plural(best.delta - myDelta + 1, "more scan")} takes the lead`
            : null,
      })
    }
  }

  {
    const c = enabled("beatPersonalBest")
    if (c) {
      const bestPrior = me != null ? bestWeekBefore(me, weekStart.getTime()) : 0
      if (bestPrior === 0) {
        pushLive("beatPersonalBest", c, {
          progressPct: null,
          progressLabel: `${plural(myWeek, "scan")} this week`,
          standingLabel: "No prior week to beat yet — this week sets your bar",
          endsLabel: endsThisWeek(),
          nudge: null,
        })
      } else {
        const target = bestPrior + 1
        pushLive("beatPersonalBest", c, {
          progressPct: Math.min(100, Math.round((myWeek / target) * 100)),
          progressLabel: `${myWeek} of ${plural(target, "scan")}`,
          standingLabel: myWeek > bestPrior ? "Personal best beaten" : `Best week: ${bestPrior}`,
          endsLabel: endsThisWeek(),
          nudge:
            myWeek > bestPrior
              ? null
              : `${plural(target - myWeek, "more scan")} beats your best week = $${c.amount}`,
        })
      }
    }
  }

  {
    const c = enabled("streakBonus")
    if (c) {
      const sd = c.streakDays ?? COMPETITION_DEFAULTS.streakBonus.defaultStreakDays ?? 5
      const cur = me != null ? currentWorkdayStreak(dayStats, me, today) : 0
      pushLive("streakBonus", c, {
        progressPct: Math.min(100, Math.round((cur / sd) * 100)),
        progressLabel: `${cur} of ${plural(sd, "workday")}`,
        standingLabel: cur >= sd ? `Streak hit — $${c.amount} earned` : null,
        endsLabel: "Ongoing",
        nudge: cur >= sd ? null : `${plural(sd - cur, "more workday")} = $${c.amount}`,
      })
    }
  }

  {
    const c = enabled("teamGoal")
    if (c) {
      const th = thresholdFor("teamGoal", c)
      const window = c.window ?? COMPETITION_DEFAULTS.teamGoal.defaultWindow ?? "weekly"
      const windowStart = window === "monthly" ? startOfMonth(now) : weekStart
      let total = 0
      for (const s of scans) {
        if (s.at >= windowStart) total++
      }
      pushLive("teamGoal", c, {
        progressPct: Math.min(100, Math.round((total / th) * 100)),
        progressLabel: `${total} of ${th} team scans`,
        standingLabel: total >= th ? `Target hit — $${c.amount} each` : null,
        endsLabel: window === "monthly" ? endsThisMonth() : endsThisWeek(),
        nudge: total >= th ? null : `${total >= th * 0.8 ? "Almost there — " : ""}${th - total} to go`,
      })
    }
  }

  // ── WINS (closed periods only, trailing 8 weeks) ─────────────────────
  const wins: CompetitionWin[] = []
  const seen = new Set<string>()
  const addWin = (w: CompetitionWin) => {
    const k = `${w.key}|${w.periodKey}|${w.userId}`
    if (seen.has(k)) return
    seen.add(k)
    wins.push(w)
  }

  // Closed days: yesterday back across the horizon.
  for (let i = 1; i <= dailyLookback; i++) {
    const day = new Date(today)
    day.setDate(day.getDate() - i)
    const dk = dateKey(day)
    const dayScans = scansByDay.get(dk)
    if (!dayScans || dayScans.length === 0) continue
    const users = dayStats.get(dk)!

    const champ = enabled("dailyScanChampion")
    if (champ) {
      const winner = topOfPeriod(users)
      if (winner) {
        addWin({
          key: "dailyScanChampion",
          userId: winner.uid,
          amount: champ.amount,
          periodKey: dk,
          wonAt: winner.stats.last,
          label: `Daily scan champion — ${shortDate(day)}`,
        })
      }
    }

    const first = enabled("firstScanOfDay")
    if (first) {
      addWin({
        key: "firstScanOfDay",
        userId: dayScans[0].userId,
        amount: first.amount,
        periodKey: dk,
        wonAt: dayScans[0].at,
        label: `First scan of the day — ${shortDate(day)}`,
      })
    }

    const hot = enabled("hotStreak")
    if (hot) {
      const th = thresholdFor("hotStreak", hot)
      for (const [uid, st] of users) {
        if (st.count < th) continue
        addWin({
          key: "hotStreak",
          userId: uid,
          amount: hot.amount,
          periodKey: dk,
          wonAt: nthScanAt(dayScans, uid, th) ?? st.last,
          label: `Hot streak bonus — ${shortDate(day)}`,
        })
      }
    }

    const fast = enabled("fastStart")
    if (fast) {
      const th = thresholdFor("fastStart", fast)
      const winner = firstToReach(dayScans, th)
      if (winner) {
        addWin({
          key: "fastStart",
          userId: winner.userId,
          amount: fast.amount,
          periodKey: dk,
          wonAt: winner.at,
          label: `Fast start bonus — ${shortDate(day)}`,
        })
      }
    }
  }

  // Closed weeks: last Monday back across the horizon. Weekly-cadence wins
  // settle at the week's CLOSE (Sun 23:59:59.999), not the winning scan —
  // otherwise, under monthly payout cadence, a win from a week straddling the
  // month boundary could backdate into an already-approved month (finding 3).
  for (let w = 1; w <= weeklyLookback; w++) {
    const wkStart = new Date(weekStart)
    wkStart.setDate(wkStart.getDate() - 7 * w)
    const wk = dateKey(wkStart)
    const wkEndExclusive = new Date(wkStart)
    wkEndExclusive.setDate(wkEndExclusive.getDate() + 7)
    const weekClose = new Date(wkEndExclusive.getTime() - 1)
    const users = weekStats.get(wk)

    const volume = enabled("weeklyVolumeLeaderboard")
    if (volume && users) {
      const qualifier =
        volume.qualifier ?? COMPETITION_DEFAULTS.weeklyVolumeLeaderboard.defaultQualifier
      const label = `Weekly volume leaderboard — ${weekOfLabel(wkStart)}`
      if (qualifier === "above_target") {
        // "Above target" pays everyone who beats the module's weekly scan
        // target — the manager-set threshold, or the registry default when
        // they enabled the qualifier without setting one.
        const th = thresholdFor("weeklyVolumeLeaderboard", volume)
        for (const [uid, st] of users) {
          if (st.count >= th) {
            addWin({ key: "weeklyVolumeLeaderboard", userId: uid, amount: volume.amount, periodKey: wk, wonAt: weekClose, label })
          }
        }
      } else {
        const ranked = rankPeriod(users)
        const winners = qualifier === "top_3" ? ranked.slice(0, 3) : ranked.slice(0, 1)
        for (const win of winners) {
          addWin({ key: "weeklyVolumeLeaderboard", userId: win.uid, amount: volume.amount, periodKey: wk, wonAt: weekClose, label })
        }
      }
    }

    const comeback = enabled("comebackChallenge")
    if (comeback && users) {
      const prevStart = new Date(wkStart)
      prevStart.setDate(prevStart.getDate() - 7)
      const prevKey = dateKey(prevStart)
      let best: { uid: number; delta: number; stats: UserPeriodStats } | null = null
      for (const [uid, st] of users) {
        const delta = st.count - countFor(weekStats, prevKey, uid)
        if (delta <= 0) continue
        if (
          !best ||
          delta > best.delta ||
          (delta === best.delta && st.first < best.stats.first)
        ) {
          best = { uid, delta, stats: st }
        }
      }
      if (best) {
        addWin({
          key: "comebackChallenge",
          userId: best.uid,
          amount: comeback.amount,
          periodKey: wk,
          wonAt: weekClose,
          label: `Comeback challenge — ${weekOfLabel(wkStart)}`,
        })
      }
    }

    const pb = enabled("beatPersonalBest")
    if (pb && users) {
      for (const [uid, st] of users) {
        const prior = bestWeekBefore(uid, wkStart.getTime())
        if (prior > 0 && st.count > prior) {
          addWin({
            key: "beatPersonalBest",
            userId: uid,
            amount: pb.amount,
            periodKey: wk,
            wonAt: weekClose,
            label: `Beat your personal best — ${weekOfLabel(wkStart)}`,
          })
        }
      }
    }
  }

  // Streak bonus — fires the day the run's length first reaches the
  // configured streakDays, including today (the one exception to the
  // closed-periods rule). One win per achieved run.
  const streak = enabled("streakBonus")
  if (streak) {
    const sd = streak.streakDays ?? COMPETITION_DEFAULTS.streakBonus.defaultStreakDays ?? 5
    const trailingCutoff = new Date(today)
    trailingCutoff.setDate(trailingCutoff.getDate() - dailyLookback)
    const uids = new Set(scans.map((s) => s.userId))
    for (const uid of uids) {
      // Walk far enough back that a run crossing sd inside the trailing
      // window is caught even if it started before it.
      let run = 0
      for (let i = dailyLookback + 90; i >= 0; i--) {
        const day = new Date(today)
        day.setDate(day.getDate() - i)
        if (!WORKDAY_INDICES.has(day.getDay())) continue
        const dk = dateKey(day)
        const st = dayStats.get(dk)?.get(uid)
        if (!st) {
          // Today with no scan yet doesn't break the run — the day is open.
          if (i !== 0) run = 0
          continue
        }
        run++
        if (run === sd && day >= trailingCutoff) {
          addWin({
            key: "streakBonus",
            userId: uid,
            amount: streak.amount,
            periodKey: dk,
            wonAt: st.first,
            label: `Streak bonus — ${sd} workdays (${shortDate(day)})`,
          })
        }
      }
    }
  }

  const team = enabled("teamGoal")
  if (team) {
    const th = thresholdFor("teamGoal", team)
    const window = team.window ?? COMPETITION_DEFAULTS.teamGoal.defaultWindow ?? "weekly"
    const windows: { start: Date; end: Date; periodKey: string; label: string }[] = []
    if (window === "weekly") {
      for (let w = 1; w <= weeklyLookback; w++) {
        const start = new Date(weekStart)
        start.setDate(start.getDate() - 7 * w)
        const end = new Date(start)
        end.setDate(end.getDate() + 7)
        windows.push({
          start,
          end,
          periodKey: dateKey(start),
          label: `Team goal bonus — ${weekOfLabel(start)}`,
        })
      }
    } else {
      // Closed months back across the horizon (≥2 by default).
      for (let m = 1; m <= monthlyLookback; m++) {
        const start = new Date(today.getFullYear(), today.getMonth() - m, 1)
        const end = new Date(today.getFullYear(), today.getMonth() - m + 1, 1)
        windows.push({
          start,
          end,
          periodKey: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`,
          label: `Team goal bonus — ${MONTH_SHORT[start.getMonth()]} ${start.getFullYear()}`,
        })
      }
    }
    for (const win of windows) {
      const inWindow = scans.filter((s) => s.at >= win.start && s.at < win.end)
      if (inWindow.length < th) continue
      // A team goal is "won" when its window closes and the total is tallied,
      // not at the threshold-crossing scan — so it can never backdate into an
      // already-approved payout period (finding 3).
      const wonAt = new Date(win.end.getTime() - 1)
      const paidUsers = new Set(inWindow.map((s) => s.userId))
      for (const uid of paidUsers) {
        addWin({
          key: "teamGoal",
          userId: uid,
          amount: team.amount,
          periodKey: win.periodKey,
          wonAt,
          label: win.label,
        })
      }
    }
  }

  wins.sort((a, b) => a.wonAt.getTime() - b.wonAt.getTime())
  return { live, wins }
}

// First scan (chronological) at which any single advisor's cumulative
// day-count reaches `threshold`. Null when nobody got there.
function firstToReach(dayScans: Scan[], threshold: number): Scan | null {
  const running = new Map<number, number>()
  for (const s of dayScans) {
    const c = (running.get(s.userId) ?? 0) + 1
    running.set(s.userId, c)
    if (c >= threshold) return s
  }
  return null
}

// Time of a user's nth scan within an (ascending) day list.
function nthScanAt(dayScans: Scan[], uid: number, n: number): Date | null {
  let c = 0
  for (const s of dayScans) {
    if (s.userId !== uid) continue
    c++
    if (c === n) return s.at
  }
  return null
}

// Period entries sorted by count desc; ties broken by earliest first
// submission in the period (the spec's tie rule).
function rankPeriod(
  users: Map<number, UserPeriodStats>,
): { uid: number; stats: UserPeriodStats }[] {
  return [...users.entries()]
    .map(([uid, stats]) => ({ uid, stats }))
    .sort(
      (a, b) =>
        b.stats.count - a.stats.count || a.stats.first.getTime() - b.stats.first.getTime(),
    )
}

function topOfPeriod(
  users: Map<number, UserPeriodStats>,
): { uid: number; stats: UserPeriodStats } | null {
  return rankPeriod(users)[0] ?? null
}

// Consecutive-workday streak ending today (today itself may still be open —
// a zero today doesn't break the run, matching advisor-stats' computeStreak).
function currentWorkdayStreak(
  dayStats: Map<string, Map<number, UserPeriodStats>>,
  uid: number,
  today: Date,
): number {
  let streak = 0
  for (let i = 0; i < 365; i++) {
    const d = new Date(today)
    d.setDate(d.getDate() - i)
    if (!WORKDAY_INDICES.has(d.getDay())) continue
    const hit = (dayStats.get(dateKey(d))?.get(uid)?.count ?? 0) > 0
    if (hit) streak++
    else if (i !== 0) break
  }
  return streak
}
